import { type PropsWithChildren } from 'react';
import { View } from 'react-native';
import { clsx } from 'clsx';
import { fasImageStack } from 'shared/@generated/icons/fasImageStack';
import { Icon } from 'shared/components/icon';
import { type IllustrationScale } from './illustration-scene';
import { illustrationPalette as palette } from './illustration-palette';
import type { IllustrationImagePalette } from './illustration-palette';

export function BrowserWindow({
  s,
  left,
  top,
  width,
  height,
  addressAlign = 'center',
  children,
}: PropsWithChildren<{
  s: IllustrationScale;
  left: number;
  top: number;
  width: number;
  height: number;
  addressAlign?: 'center' | 'left';
}>) {
  return (
    <View
      className="border-brand-brown-dark bg-brand-cream-light absolute overflow-hidden"
      style={{
        left: s(left),
        top: s(top),
        width: s(width),
        height: s(height),
        borderWidth: s(1.5),
        borderRadius: s(7),
      }}
    >
      <View
        className="border-brand-brown-dark/15 bg-brand-cream flex-row items-center"
        style={{ height: s(14), paddingHorizontal: s(7), gap: s(3), borderBottomWidth: s(0.75) }}
      >
        {[0, 1, 2].map((dot) => (
          <View
            key={dot}
            className="bg-brand-brown-dark/30 rounded-full"
            style={{ width: s(2.5), height: s(2.5) }}
          />
        ))}
        <View
          testID="illustration-browser-address"
          className={clsx('bg-brand-brown-dark/10', addressAlign === 'center' && 'mx-auto')}
          style={{
            width: s(width * 0.35),
            height: s(3),
            borderRadius: s(2),
            marginLeft: addressAlign === 'left' ? s(4) : undefined,
          }}
        />
      </View>
      <View className="relative flex-1">{children}</View>
    </View>
  );
}

export function ImageTile({
  s,
  width,
  height,
  className = 'bg-brand-cream-dark',
  tone = 'sage',
  simple = false,
  colors,
  motif = 'landscape',
}: {
  s: IllustrationScale;
  width: number;
  height: number;
  className?: string;
  tone?: 'sage' | 'sky' | 'peach' | 'lilac';
  simple?: boolean;
  colors?: IllustrationImagePalette;
  motif?: 'landscape' | 'mountain';
}) {
  return (
    <View
      testID={motif === 'mountain' ? 'illustration-mountain' : undefined}
      className={clsx('relative items-center justify-center overflow-hidden', className)}
      style={{
        width: s(width),
        height: s(height),
        borderRadius: s(4),
        backgroundColor: colors?.background ?? palette[tone],
      }}
    >
      {!simple && (
        <>
          <View
            className="absolute rounded-full"
            style={{
              left: s(width * (motif === 'mountain' ? 0.73 : 0.14)),
              top: s(height * 0.15),
              width: s(height * 0.19),
              height: s(height * 0.19),
              backgroundColor: colors?.spot ?? (tone === 'peach' ? palette.paper : palette.yellow),
            }}
          />
          {motif === 'mountain' ? (
            <>
              <View
                className="absolute"
                style={{
                  left: s(-width * 0.06),
                  top: s(height * 0.34),
                  width: 0,
                  height: 0,
                  borderLeftWidth: s(width * 0.38),
                  borderRightWidth: s(width * 0.38),
                  borderBottomWidth: s(height * 0.78),
                  borderLeftColor: 'transparent',
                  borderRightColor: 'transparent',
                  borderBottomColor:
                    colors?.far ?? (tone === 'peach' ? palette.clay : palette.green),
                }}
              />
              <View
                className="absolute"
                style={{
                  left: s(width * 0.26),
                  top: s(height * 0.5),
                  width: 0,
                  height: 0,
                  borderLeftWidth: s(width * 0.5),
                  borderRightWidth: s(width * 0.5),
                  borderBottomWidth: s(height * 0.75),
                  borderLeftColor: 'transparent',
                  borderRightColor: 'transparent',
                  borderBottomColor: colors?.near ?? palette.forest,
                }}
              />
            </>
          ) : (
            <>
              <View
                className="absolute"
                style={{
                  left: s(-width * 0.12),
                  top: s(height * 0.58),
                  width: s(width * 0.9),
                  height: s(height),
                  borderTopLeftRadius: s(height * 0.7),
                  borderTopRightRadius: s(height * 0.8),
                  backgroundColor: colors?.far ?? (tone === 'peach' ? palette.clay : palette.blue),
                  transform: [{ rotate: '-20deg' }],
                }}
              />
              <View
                className="absolute"
                style={{
                  left: s(width * 0.35),
                  top: s(height * 0.48),
                  width: s(width * 0.95),
                  height: s(height),
                  borderTopLeftRadius: s(height * 0.8),
                  borderTopRightRadius: s(height * 0.6),
                  backgroundColor:
                    colors?.near ?? (tone === 'lilac' ? palette.violet : palette.forest),
                  transform: [{ rotate: '18deg' }],
                }}
              />
            </>
          )}
        </>
      )}
      {motif === 'landscape' && (
        <Icon
          icon={fasImageStack}
          className={simple ? 'text-brand-brown/35' : 'absolute text-white/70'}
          style={{
            ...(simple ? {} : { right: s(width * 0.1), bottom: s(height * 0.12) }),
            width: s(Math.min(width, height) * (simple ? 0.32 : 0.2)),
            height: s(Math.min(width, height) * (simple ? 0.32 : 0.2)),
          }}
        />
      )}
    </View>
  );
}

export function TextLine({
  s,
  width,
  height = 3,
  className = 'bg-brand-brown-dark/35',
}: {
  s: IllustrationScale;
  width: number;
  height?: number;
  className?: string;
}) {
  return (
    <View
      className={className}
      style={{ width: s(width), height: s(height), borderRadius: s(height / 2) }}
    />
  );
}
